import React, { useRef } from 'react';
import {
  clipRectPct, parseAiTags, adaptiveTicks, playheadPillLabel, offsetFromFraction, edgeClampLeft,
} from '../util/mcpbTimeline';

// Keep an absolutely-positioned label (tick or playhead pill) fully inside the lane at the edges:
// flush-left at 0%, flush-right at 100%, centered elsewhere. (tablet/desktop per-cam bands.)
const edgeAnchor = (pct) => (pct <= 1 ? 'translateX(0)' : pct >= 99 ? 'translateX(-100%)' : 'translateX(-50%)');
// Mobile union band (pillOwnRow): clamp the label CENTER so it never clips off the page near an edge.
// Insets are >= half the widest label so the full box stays on-screen (measured on Rosas @390px:
// pill 'MMM D HH:MM:SS' ~96-103px, 'HH:MM' tick ~27px — David 2026-08-04).
const PILL_EDGE_INSET = 56; // px
const TICK_EDGE_INSET = 16; // px
// Center-anchored (translateX(-50%)) inline position: edge-clamped on the mobile union band, else the
// legacy per-pct edgeAnchor flush (tablet/desktop unchanged).
const labelPos = (pct, ownRow, insetPx) => (ownRow
  ? { left: edgeClampLeft(pct, insetPx), transform: 'translateX(-50%)' }
  : { left: `${pct}%`, transform: edgeAnchor(pct) });

/**
 * Timeline — the composite playback lane (mock `.timeline`). Clip rects positioned by time over the
 * committed window (cached coloring is a v2 hook; smart-fill desaturated), AI dots from each clip's
 * sTags (visibility-gated), evenly-spaced ticks, and the playhead marker driven by the shared offset.
 * Tap or drag the lane to seek (pointer = mouse + touch).
 *
 * conf=1 (1f) = ONE band. conf>1 (2a-4) = ONE BAND PER CHANNEL stacked in the lane, each with its own
 * cam color + that channel's clips/AI dots, sharing the single playhead + ticks + seek — pass `tracks`
 * (`[{clips, colorVar}]`, one per filled slot). Cluster badges are Phase 2b.
 *
 * Presentational: the parent owns the playhead (offsetSec) + the seek + the visibility state.
 */
const BAND_PX = 16; // per-band lane height in the multi-band (conf>1) layout

const Timeline = ({
  clips = [], tracks, windowStartTs, windowLenSec, offsetSec = 0, onSeek,
  colorVar, visibility, notice = null, cacheByClip = {}, pillOwnRow = false,
  hidePlayheadLabel = false, hoveredRow = null, onBandHover,
}) => {
  const barRef = useRef(null);
  const draggingRef = useRef(false);
  const ticks = adaptiveTicks(windowStartTs, windowLenSec);
  const playheadPct = windowLenSec > 0 ? Math.max(0, Math.min(100, (offsetSec / windowLenSec) * 100)) : 0;
  const hasWindow = !!windowStartTs && windowLenSec > 0;
  // One band per channel when `tracks` is given (conf>1); otherwise the single 1f band.
  const bands = (tracks && tracks.length) ? tracks : [{ clips, colorVar }];
  const multi = bands.length > 1;

  const seekFromClientX = (clientX) => {
    const bar = barRef.current;
    if (!bar || !onSeek) return;
    const r = bar.getBoundingClientRect();
    const frac = r.width > 0 ? (clientX - r.left) / r.width : 0;
    onSeek(offsetFromFraction(frac, windowLenSec));
  };
  // Mouse drag-scrub + touch tap/drag (jsdom-friendly; pointer events drop clientX under jsdom).
  const onMouseDown = (e) => { draggingRef.current = true; seekFromClientX(e.clientX); };
  const onMouseMove = (e) => { if (draggingRef.current) seekFromClientX(e.clientX); };
  const endDrag = () => { draggingRef.current = false; };
  const touchX = (e) => (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
  const onTouchStart = (e) => seekFromClientX(touchX(e));
  const onTouchMove = (e) => seekFromClientX(touchX(e));

  return (
    <div className="mcpb-timeline">
      {/* P-N3: dark-red playhead time-pill ('MMM D HH:MM:SS'). `pillOwnRow` (the mobile union band) gives
          it a DEDICATED row ABOVE the ruler so it never overlaps the tick labels (David 2026-08-03);
          otherwise it rides over the ticks (tablet/desktop per-cam bands — unchanged). Rendered before the
          ticks so its own row stacks above them. */}
      {hasWindow && !hidePlayheadLabel && (
        <div className={pillOwnRow ? 'mcpb-timeline__pill-row' : undefined}>
          <div
            className="mcpb-timeline__pill"
            style={labelPos(playheadPct, pillOwnRow, PILL_EDGE_INSET)}
          >
            {playheadPillLabel(windowStartTs, offsetSec)}
          </div>
        </div>
      )}
      {/* P-N4: clock-aligned ticks, absolutely positioned by their time (not evenly spaced). */}
      <div className="mcpb-timeline__ticks">
        {ticks.map((t, i) => (
          <span
            key={i}
            className="mcpb-timeline__tick"
            style={labelPos(t.pct, pillOwnRow, TICK_EDGE_INSET)}
          >
            {t.label}
          </span>
        ))}
      </div>
      <div
        className={`mcpb-timeline__bar${multi ? ' mcpb-timeline__bar--multi' : ''}`}
        ref={barRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        role="slider"
        aria-label="Timeline — tap or drag to seek"
        aria-valuemin={0}
        aria-valuemax={Math.round(windowLenSec || 0)}
        aria-valuenow={Math.round(offsetSec)}
        tabIndex={0}
        style={multi ? { height: bands.length * BAND_PX + 4 } : undefined}
      >
        {bands.map((band, bi) => (
          <div
            key={`band-${bi}`}
            className={`mcpb-timeline__band${band.dimmed ? ' mcpb-timeline__band--dimmed' : ''}${multi && hoveredRow === band.slotIndex ? ' mcpb-timeline__band--hover' : ''}`}
            style={{ '--clip-cam-color': 'var(--mcpb-event-color)' }}
            onMouseEnter={multi && onBandHover ? () => onBandHover(band.slotIndex) : undefined}
            onMouseLeave={multi && onBandHover ? () => onBandHover(null) : undefined}
          >
            {(band.name || band.num) && (
              <span
                className="mcpb-timeline__band-label"
                title={`${band.num ? band.num + '  ' : ''}${band.name}`}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {band.num ? <span className="mcpb-timeline__band-num">{band.num}</span> : null}
                {band.name}
              </span>
            )}
            {(band.clips || []).map((clip) => {
              const rect = clipRectPct(clip, windowStartTs, windowLenSec);
              if (!rect) return null;
              const sf = !!clip.fSmartFill;
              // Cache shading (dview MCTimelineBar.setClipCloudCache) applies ONLY to LEGACY cloud clips
              // (pbctrl pull-on-demand): the band starts BRIGHT (cam color = "available, fetch on demand")
              // and a DARKER fill grows left→right 0→100% as it caches, ending fully dark = cached.
              // fCached (from the events) = already cached; cacheByClip carries the in-flight/newly-cached.
              // DRAGON clips (directMedia — served complete off engine_backend, no pull) and LOCAL NVR
              // clips have NO cache concept, so they render SOLID (no shading).
              const hasCacheState = !!clip.fCloudVS && !clip.directMedia;
              const cachePct = hasCacheState
                ? (clip.fCached ? 100 : Math.max(0, Math.min(100, Number(cacheByClip[clip.bID]) || 0)))
                : 0;
              return (
                <div
                  key={`c-${bi}-${clip.bID}`}
                  className={`mcpb-timeline__clip${sf ? ' mcpb-timeline__clip--sf' : ''}${hasCacheState ? ' mcpb-timeline__clip--cloud' : ''}`}
                  style={{ left: `${rect.left}%`, width: `${rect.width}%` }}
                >
                  {hasCacheState && cachePct > 0 && (
                    <span className="mcpb-timeline__clip-cache" style={{ width: `${cachePct}%` }} />
                  )}
                </div>
              );
            })}
            {(band.clips || []).map((clip) => {
              const tags = parseAiTags(clip.sTags);
              if (!tags.length) return null;
              const type = tags[0]; // first tag → dot color (inventory §7)
              if (visibility && !visibility[type]) return null;
              const rect = clipRectPct(clip, windowStartTs, windowLenSec);
              if (!rect) return null;
              return (
                <span
                  key={`d-${bi}-${clip.bID}`}
                  className={`mcpb-timeline__dot mcpb-ai-${type}`}
                  style={{ left: `${rect.left}%` }}
                  title={type}
                />
              );
            })}
          </div>
        ))}
        <div className="mcpb-timeline__playhead" style={{ left: `${playheadPct}%` }} />
      </div>
      {notice && (
        <div
          className={`mcpb-timeline__notice mcpb-timeline__notice--${notice.tone || 'empty'}`}
          role={notice.tone === 'error' ? 'alert' : 'status'}
        >
          {notice.message}
        </div>
      )}
    </div>
  );
};

// "Show on timeline" marker visibility — visibility-only (no refetch); default all-on (inventory §8).
// Person/Vehicle/Alert gate the AI dots; Archived/Marked-for-Export gate their markers (v2 visual).
const VIZ_TYPES = [
  { key: 'person', label: 'Person', dot: 'var(--ai-person)' },
  { key: 'vehicle', label: 'Vehicle', dot: 'var(--ai-vehicle)' },
  { key: 'alert', label: 'Alert', dot: 'var(--ai-alert)' },
  { key: 'archived', label: 'Archived', path: 'M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5h13.75l.81.97H4.34L5.12 5z' },
  { key: 'export', label: 'Marked for Export', path: 'M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z' },
];

export const TimelineVisibilityRow = ({ visibility = {}, onToggle, onShowAll }) => {
  const anyHidden = VIZ_TYPES.some((t) => !visibility[t.key]);
  return (
    <div className="mcpb-timeline-viz">
      <div className="mcpb-timeline-viz__head">
        <span className="mcpb-timeline-viz__label">Show on timeline</span>
        {anyHidden && (
          <button className="mcpb-timeline-viz__showall" type="button" onClick={onShowAll}>Show all</button>
        )}
      </div>
      <div className="mcpb-timeline-viz__chips">
        {VIZ_TYPES.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`mcpb-timeline-viz__chip${visibility[t.key] ? '' : ' mcpb-timeline-viz__chip--off'}`}
            aria-pressed={!!visibility[t.key]}
            title={`${visibility[t.key] ? 'Hide' : 'Show'} ${t.label} markers`}
            onClick={() => onToggle && onToggle(t.key)}
          >
            {t.dot
              ? <span className="mcpb-timeline-viz__dot" style={{ background: t.dot }} />
              : <svg viewBox="0 0 24 24" className="mcpb-timeline-viz__svg"><path d={t.path} /></svg>}
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Timeline;
